import React, { useMemo } from 'react';

import { calculateBillMetrics } from '@/features/billing/utils';
import { EMPTY_RESULT } from '@/shared/constants';
import type { MappedBill, PaymentMode } from '@/shared/types/bill';
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { format } from 'date-fns';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    color: '#000',
    backgroundColor: '#fff',
    fontFamily: 'Courier-Bold',
  },

  // ---------------- HEADER ----------------
  headerContainer: {
    display: 'flex',
    flexDirection: 'column',
    borderBottom: '1 solid #000',
    marginBottom: 8,
    paddingBottom: 6,
    width: '100%',
  },

  clinicHeader: {
    marginBottom: 8,
    paddingBottom: 6,
  },

  clinicName: {
    fontSize: 14,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    alignItems: 'center',
    textAlign: 'center',
  },
  clinicAddress: {
    fontSize: 10,
    marginTop: 4,
    alignItems: 'center',
    textAlign: 'center',
  },
  contact: {
    fontSize: 10,
    marginTop: 4,
    alignItems: 'center',
    textAlign: 'center',
    letterSpacing: 0.5,
  },

  // ---------------- INFO ROWS ----------------
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: 10,
  },

  infoSection: {
    width: '48%',
    marginBottom: 4,
    rowGap: 4,
  },

  infoLine: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 3,
  },

  infoLabel: {
    width: '35%', // all labels align left
    fontWeight: 'bold',
  },
  infoColon: {
    width: '5%', // middle aligned ":" column
    textAlign: 'center',
  },
  infoValue: {
    width: '60%', // value column
  },

  // ---------------- TITLE ----------------
  titleContainer: {
    alignItems: 'center',
    paddingTop: 10,
    paddingBottom: 10,
  },

  title: {
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
    borderBottom: '2 solid #000',
  },

  // ---------------- TABLE ----------------
  table: {},

  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f0f0f0',
    borderTop: '1 solid #000',
    borderBottom: '1 solid #000',
    paddingVertical: 6,
  },

  tableRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #e0e0e0',
    paddingVertical: 8,
  },

  colSNo: {
    width: '8%',
    paddingLeft: 8,
    fontSize: 9,
  },

  colServices: {
    width: '35%',
    paddingLeft: 5,
    fontSize: 9,
  },

  colQty: {
    width: '12%',
    textAlign: 'center',
    fontSize: 9,
  },

  colPrice: {
    width: '15%',
    textAlign: 'right',
    paddingRight: 8,
    fontSize: 9,
  },

  colDiscount: {
    width: '15%',
    textAlign: 'right',
    paddingRight: 8,
    fontSize: 9,
  },

  colAmount: {
    width: '15%',
    textAlign: 'right',
    paddingRight: 8,
    fontSize: 9,
  },

  headerText: {
    fontWeight: 'bold',
    fontSize: 9,
  },

  // ---------------- FOOTER TOTALS ----------------
  totalsContainer: {
    marginTop: 20,
    alignSelf: 'flex-end',
    width: '40%',
    borderTop: '2 solid #000',
    paddingTop: 10,
  },

  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
    fontSize: 10,
  },

  totalLabel: {
    fontWeight: 'bold',
    textAlign: 'right',
    paddingRight: 20,
  },

  totalValue: {
    fontWeight: 'bold',
    textAlign: 'right',
    minWidth: 80,
  },

  finalTotal: {
    borderTop: '2 solid #000',
    paddingTop: 8,
    marginTop: 5,
  },

  balanceError: {
    color: 'red',
  },

  // ---------------- NOTES & SIGNATURE ----------------
  notesSignatureContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
    gap: 20,
  },
  notesSection: {
    flex: 1,
    paddingRight: 10,
  },
  notesTitle: {
    fontWeight: 'bold',
    marginBottom: 4,
    fontSize: 10,
  },
  notesText: {
    fontSize: 9,
    lineHeight: 1.4,
  },
  signatureSection: {
    width: 200,
    alignItems: 'flex-end',
  },
  signatureBox: {
    borderTop: '1 solid #000',
    paddingTop: 4,
    marginTop: 40,
    width: 150,
    textAlign: 'center',
  },
  signatureLabel: {
    fontSize: 9,
    fontWeight: 'bold',
  },
});

// Table Component
const BillTable = ({ data }: { data: any[] }) => {
  return (
    <View style={styles.table}>
      {/* Table Header */}
      <View style={styles.tableHeader}>
        <Text style={[styles.colSNo, styles.headerText]}>S.No</Text>
        <Text style={[styles.colServices, styles.headerText]}>Services</Text>
        <Text style={[styles.colQty, styles.headerText]}>Qty</Text>
        <Text style={[styles.colPrice, styles.headerText]}>Price</Text>
        <Text style={[styles.colDiscount, styles.headerText]}>Discount</Text>
        <Text style={[styles.colAmount, styles.headerText]}>Amount</Text>
      </View>

      {/* Table Rows */}
      {data.map((row, index) => (
        <View key={index} style={styles.tableRow}>
          <Text style={styles.colSNo}>{row.sno}</Text>
          <Text style={styles.colServices}>{row.service}</Text>
          <Text style={styles.colQty}>{row.qty}</Text>
          <Text style={styles.colPrice}>{row.price.toFixed(2)}</Text>
          <Text style={styles.colDiscount}>{row.discount.toFixed(2)}</Text>
          <Text style={styles.colAmount}>{row.amount.toFixed(2)}</Text>
        </View>
      ))}
    </View>
  );
};

// Main Bill PDF Component
interface BillPrintPDFProps {
  bill: MappedBill;
  paymentsMode: PaymentMode[];
  clinicData: {
    clinicName: string;
    addressComponents: string;
    phoneNumbers: string;
    email: string;
    clinicTime: string;
    website: string;
  };
  patientData: {
    name: string;
    mobileNo: string;
    gender: string;
    age: string;
    address: string;
    id: string | undefined;
  };
}

const InfoRow = ({ label, value }: { label: string; value: string | number }) => (
  <View style={styles.infoLine}>
    <Text style={styles.infoLabel}>{label}</Text>
    <Text style={styles.infoColon}>:</Text>
    <Text style={styles.infoValue}>{value || EMPTY_RESULT}</Text>
  </View>
);

export const BillPrintPDF = ({ bill, clinicData, patientData }: BillPrintPDFProps) => {
  const lineItemData = bill?.lineItems?.map((lineItem, idx) => {
    const lineItemDiscount = lineItem.discount ?? 0;

    return {
      sno: idx + 1,
      service: lineItem.billableService!,
      qty: lineItem.quantity.toFixed(2),
      price: lineItem.price,
      discount: lineItemDiscount,
      amount: Math.max(lineItem.price * lineItem.quantity - lineItemDiscount, 0),
    };
  });

  const {
    discountedAmt,
    totalPaid: totalPaidAmount,
    amountDue,
    billTotalAmount,
  } = useMemo(() => calculateBillMetrics(bill), [bill]);
  const hasRefundBalance = amountDue < 0;

  const patientInfo = [
    { label: 'Patient Name', value: patientData.name },
    { label: 'Patient ID', value: patientData.id! },
    { label: 'Address', value: patientData.address },
    { label: 'Mobile No', value: patientData.mobileNo },
  ];

  const billInfo = [
    { label: 'Bill No.', value: bill.id },
    // { label: 'Cashier', value: removeDoctorTag(bill.cashier?.display) },
    { label: 'Bill Date', value: format(new Date(bill.originalDateCreated), 'dd, MMM, yyyy. hh:mma') },
    { label: 'Location', value: bill.cashPointLocation },
  ];

  return (
    <Document title={`Bill - ${patientData.name} (${patientData.id})`}>
      <Page size="A4" style={styles.page}>
        {/* Header Section */}
        <View style={styles.headerContainer}>
          <View style={styles.clinicHeader}>
            {/* Clinic name & address in center */}
            <Text style={styles.clinicName}>{clinicData.clinicName}</Text>
            <Text style={styles.clinicAddress}>{clinicData.addressComponents}</Text>
            <Text style={styles.contact}>{`Ph.no: ${clinicData.phoneNumbers}, Email: ${clinicData.email}`}</Text>
          </View>

          {/* Info sections below */}
          <View style={styles.infoRow}>
            {/* Left: Patient Info */}
            <View style={styles.infoSection}>
              {patientInfo?.map((item, idx) => (
                <InfoRow key={`${idx}-${item?.label}`} label={item.label} value={item.value} />
              ))}
            </View>

            {/* Right: Bill Info */}
            <View style={styles.infoSection}>
              {billInfo?.map((item, idx) => (
                <InfoRow key={`${idx}-${item?.label}`} label={item.label} value={item.value} />
              ))}
            </View>
          </View>
        </View>

        {/* Title */}
        <View style={styles.titleContainer}>
          <Text style={styles.title}>Bill Summary</Text>
        </View>

        {/* Table */}
        <BillTable data={lineItemData} />

        {/* Totals Section */}
        <View style={styles.totalsContainer}>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Total Amount:</Text>
            <Text style={styles.totalValue}>{billTotalAmount.toFixed(2)}</Text>
          </View>

          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Paid Amount:</Text>
            <Text style={styles.totalValue}>{totalPaidAmount.toFixed(2)}</Text>
          </View>

          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Discount Amount:</Text>
            <Text style={styles.totalValue}>{discountedAmt.toFixed(2)}</Text>
          </View>

          {hasRefundBalance && (
            <View style={styles.totalRow}>
              <Text style={[styles.totalLabel, styles.balanceError]}>Balance to Patient</Text>
              <Text style={[styles.totalValue, styles.balanceError]}>{hasRefundBalance ? -amountDue : amountDue}</Text>
            </View>
          )}

          <View style={[styles.totalRow, styles.finalTotal]}>
            <Text style={styles.totalLabel}>Amount Due</Text>
            <Text style={styles.totalValue}>{Math.max(amountDue, 0).toFixed(2)}</Text>
          </View>
        </View>

        <View style={styles.notesSignatureContainer}>
          <View style={styles.notesSection}>
            <Text style={styles.notesTitle}>Notes:</Text>
            <Text style={styles.notesText}>
              • Please verify all charges before leaving the billing counter.{'\n'}• Keep this bill for insurance claims
              and future reference.{'\n'}• For any queries, contact the billing department.
            </Text>
          </View>

          {/* <View style={styles.signatureSection}>
             <View style={styles.signatureBox}>
               <Text style={styles.signatureLabel}>Signature</Text>
             </View>
           </View> */}
        </View>
      </Page>
    </Document>
  );
};
